import type { CellContext, Row } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useLocation, useHistory } from 'react-router';

import type { W_Credentials_FindAllForCompany_VM } from 'ab-api/web/credentials/findCompanyCredentials';
import type { W_Employee_FindAllForCompanyTable_VM_Row } from 'ab-api/web/employee/findAllForCompanyTable';

import { isAllowed } from '@acceligentllc/shared/utils/auth';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { AccountPermissionTemplateLabel } from '@acceligentllc/shared/enums/accountPermissionTemplateOptions';
import { BlobStorageImageSizeContainer } from '@acceligentllc/shared/enums/blobStorageImageSizeContainer';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import { downloadCSV } from 'af-utils/csv.utils';

import { CredentialNotificationTypeLabel } from 'ab-enums/CredentialNotificationType.enum';
import PagePermissions from 'ab-enums/pagePermissions.enum';
import TableButtonType from 'ab-enums/tableButtonType.enum';
import TableNameEnum from 'ab-enums/tableName.enum';

import CLIENT from 'af-constants/routes/client';

import { DEFAULT_EMPLOYEE_IMAGE } from 'ab-common/constants/value';
import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import Breadcrumbs from 'af-components/Breadcrumbs';
import ImageTag from 'af-components/Image';
import type { TableRef } from 'af-components/Table';
import TableNew from 'af-components/Table';
import ActionsCell from 'af-components/Table/Cells/ActionsCell';
import EmptyCell from 'af-components/Table/Cells/EmptyCell';
import AddFilter from 'af-components/Table/Filters/AddFilter';
import type { TableProps } from 'af-components/Table/types';
import SkillsCell from 'af-components/Table6/Cells/SkillsCell';
import TabNavigation from 'af-components/TabNavigation';

import * as CredentialActions from 'af-actions/credentials';
import * as EmployeeActions from 'af-actions/employee';

import type { CredentialFilter, CredentialFilterOption } from './Filters/CredentialsFilter';
import EmployeeTableAdditionalFilters from './Filters/EmployeeTableAdditionalFilters';
import { activeToCSVData, deactivatedToCSVData } from './helpers';
import styles from './styles.module.scss';

type Props = ConnectedProps<typeof connector>;

export interface AdditionalFilterType {
	id: string;
	label: string;
}

const ACTIVE_LABOR_VIEW_TAB = { ID: 1, LABEL: 'LABOR' };
const DEACTIVATED_LABOR_VIEW_TAB = { ID: 2, LABEL: 'RECENTLY DEACTIVATED' };

const TABS = [
	{ id: ACTIVE_LABOR_VIEW_TAB.ID, label: ACTIVE_LABOR_VIEW_TAB.LABEL },
	{ id: DEACTIVATED_LABOR_VIEW_TAB.ID, label: DEACTIVATED_LABOR_VIEW_TAB.LABEL },
];

const breadcrumbs = [{ label: 'Labor' }];

export const MAIN_FILTER_OPTION_IDS = {
	DATE: 1,
};

export const MAIN_FILTER_OPTIONS = [
	{
		id: MAIN_FILTER_OPTION_IDS.DATE,
		label: 'Date',
	},
];

const Employees: React.FC<Props> = (props) => {
	const {
		companyName,
		findAllForCompanyList,
		hasManagePermission,
		findAllDeactivatedForCompanyTable,
		findAllForCompanyTable,
		findAllDeactivatedForCompanyList,
		findCompanyCredentials,
	} = props;

	const tableRef = React.useRef<TableRef<W_Employee_FindAllForCompanyTable_VM_Row>>(null);
	const isInitialMount = React.useRef(true);

	const [activeTab, setActiveTab] = React.useState<number>(1);
	const [credentials, setCredentials] = React.useState<Nullable<W_Credentials_FindAllForCompany_VM['credentials']>>(null);

	// Filter
	const [additionalFilter, setAdditionalFilter] = React.useState<AdditionalFilterType[]>([]);
	const [autoOpenFilters, setAutoOpenFilters] = React.useState(false);
	const [activeAdditionalFilter, setActiveAdditionalFilter] = React.useState<Nullable<AdditionalFilterType>>(null);

	const [credentialsFilter, setCredentialsFilter] = React.useState<Nullable<CredentialFilter>>(null);

	const location = useLocation<{ orgAlias: string; }>();
	const history = useHistory();

	// On mount
	React.useEffect(() => {
		const _initialize = async () => {
			const _credentials = await findCompanyCredentials();
			setCredentials(_credentials.credentials);
			setAdditionalFilter(_credentials.credentials.map((_cred) => ({
				id: _cred.organizationCredentialId.toString(),
				label: _cred.name,
			})));
		};
		_initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// refresh hook for additional filters
	React.useEffect(() => {
		// We want to avoid it on initial mount since it refreshes cached pagination
		if (isInitialMount.current) {
			isInitialMount.current = false;
			return;
		}

		refreshDataAndPagination();

		// Don't want refresh and autoRefresh here
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		credentialsFilter,
	]);

	const editEmployee = React.useCallback(async (original: W_Employee_FindAllForCompanyTable_VM_Row) => {
		history.push(CLIENT.COMPANY.RESOURCES.EMPLOYEE.EDIT(original.id.toString(), location.state.orgAlias, companyName));
	}, [companyName, history, location.state.orgAlias]);

	const previewEmployee = React.useCallback(async (original: W_Employee_FindAllForCompanyTable_VM_Row) => {
		history.push(CLIENT.COMPANY.RESOURCES.EMPLOYEE.PREVIEW(original.id.toString(), location.state.orgAlias, companyName));
	}, [companyName, history, location.state.orgAlias]);

	const statusHistoryEmployee = React.useCallback(async (original: W_Employee_FindAllForCompanyTable_VM_Row) => {
		history.push(CLIENT.COMPANY.RESOURCES.EMPLOYEE.STATUS_HISTORY(original.id.toString(), location.state.orgAlias, companyName));
	}, [companyName, history, location.state.orgAlias]);

	const onDownloadCSVClick = React.useCallback(async () => {
		if (activeTab === ACTIVE_LABOR_VIEW_TAB.ID) {
			const listViewModel = await findAllForCompanyList(tableRef!.current!.getTableQuery().filterByText);
			downloadCSV(activeToCSVData(listViewModel), `${companyName}_employees.csv`);
		} else {
			const listViewModel = await findAllDeactivatedForCompanyList(tableRef!.current!.getTableQuery());
			downloadCSV(deactivatedToCSVData(listViewModel), `${companyName}_deactivated_employees.csv`);
		}
	}, [activeTab, companyName, findAllDeactivatedForCompanyList, findAllForCompanyList]);

	const onRegisterUserClick = React.useCallback(async () => {
		history.push(
			CLIENT.COMPANY.SETTINGS.MEMBERS.INVITE(location.state.orgAlias, companyName),
			{ from: CLIENT.COMPANY.RESOURCES.EMPLOYEE.LIST(), orgAlias: location.state.orgAlias }
		);
	}, [companyName, history, location.state.orgAlias]);

	const resolveActionsButton = React.useCallback((_cell: CellContext<W_Employee_FindAllForCompanyTable_VM_Row, unknown>) => {
		const options = [
			{ onClick: () => previewEmployee(_cell.row.original), label: 'Preview' },
		];

		if (activeTab === ACTIVE_LABOR_VIEW_TAB.ID) {
			options.push({ onClick: () => editEmployee(_cell.row.original), label: 'Edit' });
			options.push({ onClick: () => statusHistoryEmployee(_cell.row.original), label: 'Status History' });
		}

		return (
			<ActionsCell
				id="actions"
				isActionDropdown={true}
				labelKey="label"
				options={options}
				valueKey="label"
			/>
		);
	}, [activeTab, editEmployee, previewEmployee, statusHistoryEmployee]);

	const getCredentialColumn = React.useCallback((_credentialColumn: W_Credentials_FindAllForCompany_VM['credentials'][0]) => ({
		id: _credentialColumn.name,
		header: _credentialColumn.name,
		accessor: _credentialColumn.name,
		sortable: false,
		size: 200,
		cell: (_cell: CellContext<W_Employee_FindAllForCompanyTable_VM_Row, string>) => {
			const credential = _cell.row.original.credentialMap[_credentialColumn.organizationCredentialId];
			if (!credential) {
				return 'N/A';
			}

			const _issuedDate = credential.issuedDate ? TimeUtils.formatDate(credential.issuedDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY) : 'N/A';
			const _endDate = TimeUtils.formatDate(credential.expirationDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);
			const _nextReminderDate = TimeUtils.formatDate(credential.nextReminderDate, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);
			const _hasExpired = credential.daysLeft <= 0;

			const _expirationClassName = _hasExpired ? styles['credential__expiration--expired'] : '';
			const value = - credential.daysLeft;

			return (
				<div>
					<div>
						Issued Date: {_issuedDate}
					</div>
					<div>
						Expiration Date: {_endDate}
					</div>
					<div className={_expirationClassName}>
						Days left: {_hasExpired ? `Expired ${value} ${value === 1 ? 'day' : 'days'} ago` : credential.daysLeft}
					</div>
					{credential.isEnabled &&
						<div>
							Next Reminder: {_nextReminderDate} {credential.reminderType !== null ? `(${CredentialNotificationTypeLabel[credential.reminderType]})` : ''}
						</div>
					}
				</div>
			);
		},
	}), []);

	const columns: TableProps<W_Employee_FindAllForCompanyTable_VM_Row>['columns'] = React.useMemo(() => [
		{
			id: 'imageUrl',
			accessor: 'imageUrl',
			enableSorting: false,
			size: 100,
			header: '',
			cell: (_cell: CellContext<W_Employee_FindAllForCompanyTable_VM_Row, string>) => (
				<div className="rt-thumbnail">
					<ImageTag
						fallbackSrc={DEFAULT_EMPLOYEE_IMAGE}
						minSize={BlobStorageImageSizeContainer.SIZE_50X50}
						src={_cell.getValue() ?? null}
						tryOriginal={true}
						tryRoot={true}
					/>
				</div>
			),
		},
		{
			id: 'id',
			header: 'ID',
			accessor: 'account.user.uniqueId',
			cell: (_cell: CellContext<W_Employee_FindAllForCompanyTable_VM_Row, string>) => _cell.row.original.code,
			enableSorting: true,
		},
		{
			id: 'fullName',
			header: 'Full Name',
			accessor: 'fullName',
			cell: (_cell: CellContext<W_Employee_FindAllForCompanyTable_VM_Row, string>) => <strong>{_cell.row.original.fullName}</strong>,
			enableSorting: true,
		},
		{
			id: 'userRole',
			header: 'User Role',
			accessor: 'account.accountTemplate',
			cell: (_cell: CellContext<W_Employee_FindAllForCompanyTable_VM_Row, string>) => {
				return <strong>{AccountPermissionTemplateLabel[_cell.row.original.accountTemplate]}</strong>;
			},
			enableSorting: true,
		},
		{
			id: 'laborType',
			header: 'Labor Type',
			accessor: 'wageRate.type',
			cell: (_cell: CellContext<W_Employee_FindAllForCompanyTable_VM_Row, string>) => _cell.row.original.wageRate.type,
			enableSorting: true,
		},
		{
			id: 'wageClassification',
			header: 'Wage Classification',
			accessor: 'wageRate.wageClassification',
			cell: (_cell: CellContext<W_Employee_FindAllForCompanyTable_VM_Row, string>) => _cell.row.original.wageRate.wageClassification,
			enableSorting: true,
		},
		{
			id: 'homeOffice',
			header: 'Home Office',
			accessor: 'account.location.nickname',
			cell: (_cell: CellContext<W_Employee_FindAllForCompanyTable_VM_Row, string>) => _cell.row.original?.location?.nickname ?? <EmptyCell />,
			enableSorting: true,
		},
		{
			id: 'skills',
			header: 'Skills',
			accessor: 'skills',
			sortable: false,
			cell: (_cell: CellContext<W_Employee_FindAllForCompanyTable_VM_Row, string>) => <SkillsCell skills={_cell.row.original.skills} />,
		},
		...(credentials ?? []).map(getCredentialColumn),
		{
			id: 'actions',
			isDisplayColumn: true,
			header: () => <EmptyCell isHeader />,
			cell: resolveActionsButton,
		},
	], [credentials, getCredentialColumn, resolveActionsButton]);

	const onRowClick = React.useCallback((row: Row<W_Employee_FindAllForCompanyTable_VM_Row>) => {
		if (row.original.id) {
			history.push(CLIENT.COMPANY.RESOURCES.EMPLOYEE.PREVIEW(row.original.id.toString(), location.state.orgAlias, companyName));
		}
	}, [companyName, history, location.state.orgAlias]);

	const buttons = React.useMemo(() => {

		const _buttons = [
			{
				type: TableButtonType.EXPORT,
				hasPermission: true,
				label: 'CSV Export',
				onClick: onDownloadCSVClick,
			},
			{
				type: TableButtonType.PRIMARY,
				hasPermission: hasManagePermission,
				label: 'Register User',
				onClick: onRegisterUserClick,
			},
		];

		if (activeTab === DEACTIVATED_LABOR_VIEW_TAB.ID) {
			_buttons.pop();
		}

		return _buttons;
	}, [activeTab, hasManagePermission, onDownloadCSVClick, onRegisterUserClick]);

	const refreshDataAndPagination = React.useCallback(() => {
		tableRef.current?.resetPagination();
	}, [tableRef]);

	const tableName = React.useMemo(() => activeTab === ACTIVE_LABOR_VIEW_TAB.ID ? TableNameEnum.LABOR : TableNameEnum.DEACTIVATED_LABOR, [activeTab]);

	const fetchRows = React.useCallback(async (tableRequestModel: TableQuery): Promise<TableContent<W_Employee_FindAllForCompanyTable_VM_Row>> => {
		const { page, pageSize, filterByText, sortBy } = tableRequestModel;
		const _additionalFilter = {
			credentialFilter: credentialsFilter ? {
				organizationCredentialId: credentialsFilter.id,
				field: {
					field: credentialsFilter.field.value,
					type: credentialsFilter.field.sort,
				},
			} : undefined,
		};

		const result = activeTab === ACTIVE_LABOR_VIEW_TAB.ID
			? await findAllForCompanyTable(
				new TableQuery({ page, pageSize, sortBy, filterByText }),
				_additionalFilter
			)
			: await findAllDeactivatedForCompanyTable(
				new TableQuery({ page, pageSize, sortBy, filterByText })
			);
		return result;
	}, [activeTab, credentialsFilter, findAllDeactivatedForCompanyTable, findAllForCompanyTable]);

	const onTabChange = React.useCallback((id: number) => setActiveTab(id), []);

	const additionalFilterOptions = React.useMemo(() => {
		return (additionalFilter ?? []).filter((option) => {
			return activeAdditionalFilter?.id !== option.id;
		});
	}, [activeAdditionalFilter, additionalFilter]);

	const addAdditionalFilter = React.useCallback((option: AdditionalFilterType) => {
		setAutoOpenFilters(true);
		setActiveAdditionalFilter(option);
	}, []);

	const changeCredentialFilters = React.useCallback((filter: CredentialFilterOption, organizationCredentialId: number) => {
		setCredentialsFilter({
			id: organizationCredentialId,
			field: filter,
		});
	}, []);

	const clearCredentialsFilter = React.useCallback(() => {
		setActiveAdditionalFilter(null);
		setCredentialsFilter(null);
	}, []);

	const resetAdditionalFilters = React.useCallback(() => {
		setActiveAdditionalFilter(null);
		setCredentialsFilter(null);
	}, []);

	const renderSpecializedFilters = React.useCallback(() => {
		return (
			<EmployeeTableAdditionalFilters
				activeAdditionalFilters={activeAdditionalFilter}
				autoOpenFilters={autoOpenFilters}
				changeCredentialFilters={changeCredentialFilters}
				clearCredentialsFilter={clearCredentialsFilter}
				resetAdditionalFilters={resetAdditionalFilters}
				selectedCredentialFilter={credentialsFilter}
			/>
		);
	}, [activeAdditionalFilter, autoOpenFilters, changeCredentialFilters, clearCredentialsFilter, resetAdditionalFilters, credentialsFilter]);

	const renderFilter = React.useCallback(() => {
		return (
			<div className={styles['filter-container']}>
				<AddFilter
					addAdditionalFilter={addAdditionalFilter}
					disabled={!!activeAdditionalFilter}
					options={additionalFilterOptions}
				/>
			</div>
		);
	}, [activeAdditionalFilter, addAdditionalFilter, additionalFilterOptions]);

	return (
		<div className="form-segment form-segment--maxi">
			<Breadcrumbs items={breadcrumbs} />
			<TabNavigation
				active={activeTab}
				onClick={onTabChange}
				tabs={TABS}
			/>
			<TableNew
				additionalFilter={renderFilter}
				buttons={buttons}
				columns={columns}
				fetch={fetchRows}
				hasSearchInput={true}
				hasSubRows={false}
				key={activeTab}
				offsetHeight={30}
				onRowClick={onRowClick}
				ref={tableRef}
				selectable={false}
				specializedFilters={renderSpecializedFilters}
				tableName={tableName}
			/>
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { userData, companyData } = state.user;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const hasManagePermission: boolean = isAllowed(
		PagePermissions.COMPANY.SETTINGS.MEMBERS.MANAGE,
		companyData.permissions,
		companyData.isCompanyAdmin,
		userData.role
	);

	return {
		hasManagePermission,
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		findAllForCompanyTable: EmployeeActions.findAllForCompanyTable,
		findAllForCompanyList: EmployeeActions.findAllForCompanyList,
		findAllDeactivatedForCompanyList: EmployeeActions.findAllDeactivatedForCompanyList,
		findAllDeactivatedForCompanyTable: EmployeeActions.findAllDeactivatedForCompanyTable,
		findCompanyCredentials: CredentialActions.findCompanyCredentials,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(Employees);
